import type { FieldMetadata, FieldProvenance } from '../../data/client';
import { formatDateTime } from '../../fields/format';
import { Icon, Tooltip } from '../../ui';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';
import type { FieldWriteMarker } from './useRecordFieldSave';

/**
 * The two things a field row may have to admit about itself.
 *
 * The write REFUSAL stays a permanent part of the row - it is the honest ending
 * of an action and must not hide behind a hover. The human-owned LOCK is a
 * standing state, and repeating its sentence under every locked field drowned
 * the card (Arif, 2026-07-28): it is now a marker with the sentence in a
 * tooltip, and the full story stays readable in the timeline.
 */

export interface HumanOwnedNoteProps {
  readonly provenance: FieldProvenance;
}

/** INV-5: a human corrected this field, so automated writers are refused. */
export function HumanOwnedNote({ provenance }: HumanOwnedNoteProps) {
  const name = provenance.displayName ?? provenance.userId;
  return (
    <Tooltip
      /* R10 (COPY-DECK-2026-09-12.md section 4): a tooltip starts with a verb, stays in
         sentence case under 75 characters, carries no period and does not repeat the
         control's name. */
      label={`Held by ${name} since ${formatDateTime(provenance.at)}; automated writers can't change it`}
    >
      {/* A real button, so keyboard focus and the aria-label come for free; it
          performs no action - its whole job is carrying the tooltip. */}
      <button
        type="button"
        aria-label={`Human-owned field, corrected by ${name}`}
        // 14px of icon, pressed at the 40px floor (`ui-foundation.md` R5): the tooltip it
        // carries is the only way to read WHO holds the field, and on a phone a 14px
        // target is a fact nobody can reach.
        className="hit-expand inline-flex w-fit cursor-help items-center text-ink-subtle"
      >
        <Icon name="blocked" size="0.85rem" />
      </button>
    </Tooltip>
  );
}

/**
 * D2: THE SYSTEM WRITES THIS FIELD AND A PERSON DOES NOT, said where a person would
 * otherwise reach for it.
 *
 * A SENTENCE RATHER THAN A DISABLED CONTROL. The record layer refuses a hand write with
 * `invalid_state`, so offering the click and answering with an error would teach the reader
 * only that the product said no. The note points at what they CAN do instead, which on an
 * enrollment is the assessment fields in the same section.
 *
 * NOT A TOOLTIP, unlike the human-owned lock next door: a lock is a standing state a reader
 * may want to look up, and this is the ANSWER to the question they are asking by looking at
 * the row.
 */
export function ComputedFieldNote() {
  return (
    <p className="text-2xs text-ink-subtle">
      Computed by the system. A judgment of your own belongs in the field beside it.
    </p>
  );
}

export interface WriteMarkerNoteProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly marker: FieldWriteMarker;
}

/**
 * The honest ending of a write that did not apply. It names the value that was
 * attempted, because "something was refused" without the value is unactionable.
 */
export function WriteMarkerNote({ objectId, recordId, field, marker }: WriteMarkerNoteProps) {
  /*
    WAVE 3 (R-B1): THE NOTE IS TEXT BESIDE THE ROW, NOT A TINTED BOX INSIDE IT.
    A bordered, filled box with a capitalised, letter-spaced heading is a web alert card
    drawn inside a kit group box row - a frame inside a frame, which the HIG's layout rule
    forbids and the owner counted as one of the surfaces that do not match. Apple says a
    validation message right next to the field in the semantic colour, and that is all this
    is now: the symbol and the state in the tone, the value under it, the reason last.
    hig: https://developer.apple.com/design/human-interface-guidelines/entering-data
  */
  if (marker.kind === 'awaiting_approval') {
    return (
      <div className="mt-1">
        <p className="flex items-center gap-1.5 text-2xs font-medium text-warning">
          <Icon name="clock" size="0.85rem" />
          Awaiting approval
        </p>
        <p className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-1.5 text-sm text-ink">
          <span className="text-ink-subtle">Proposed</span>
          <ReadOnlyFieldValue
            objectId={objectId}
            recordId={recordId}
            field={field}
            value={marker.attempted}
          />
        </p>
        <p className="mt-0.5 text-2xs text-ink-muted">
          The stored value stays as it is until someone approves this change.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-1">
      <p className="flex items-center gap-1.5 text-2xs font-medium text-danger">
        <Icon name="blocked" size="0.85rem" />
        Refused
      </p>
      <p className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-1.5 text-sm text-ink">
        <span className="text-ink-subtle">Attempted</span>
        <ReadOnlyFieldValue
          objectId={objectId}
          recordId={recordId}
          field={field}
          value={marker.attempted}
        />
      </p>
      <p className="mt-0.5 text-2xs text-ink-muted">
        {marker.heldBy === undefined
          ? 'This field is held by a human correction and was not changed.'
          : `Held by ${marker.heldBy.displayName ?? marker.heldBy.userId} since ${formatDateTime(marker.heldBy.at)}. The stored value was not changed.`}
      </p>
    </div>
  );
}
